import React, { useState, useEffect } from 'react'
import { 
  Scale, RefreshCw, CheckCircle, XCircle, AlertTriangle,
  ChevronDown, ChevronUp, HelpCircle, TrendingUp, BookOpen, FileText
} from 'lucide-react'
import { toast } from 'sonner'

const API_URL = process.env.REACT_APP_BACKEND_URL

const RECOMMANDATIONS = {
  ouverture_assureur: { label: 'Ouverture assureur', color: 'bg-blue-100 text-blue-700', icon: '🏛️' },
  amiable: { label: 'Gestion amiable', color: 'bg-teal-100 text-teal-700', icon: '🤝' },
  conservation: { label: 'Rétention', color: 'bg-slate-100 text-slate-700', icon: '📁' },
  retention: { label: 'Rétention', color: 'bg-slate-100 text-slate-700', icon: '📁' },
  attente_pieces: { label: 'Attente de pièces', color: 'bg-amber-100 text-amber-700', icon: '⏳' },
  revue_senior: { label: 'Revue senior', color: 'bg-orange-100 text-orange-700', icon: '👥' },
  contentieux: { label: 'Analyse juridique', color: 'bg-red-100 text-red-700', icon: '⚖️' },
}

const STATUTS = {
  generee: { label: 'En attente', color: 'bg-amber-100 text-amber-700' },
  suivie: { label: 'Suivie', color: 'bg-green-100 text-green-700' },
  ecartee: { label: 'Écartée', color: 'bg-red-100 text-red-700' },
}

export default function DoctrineRecommandationBlock({ sinistreId, onRecommandationUpdate, useKB = true }) {
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [recommandation, setRecommandation] = useState(null)
  const [error, setError] = useState(null)
  const [showDetails, setShowDetails] = useState(false)
  const [showValidation, setShowValidation] = useState(false)
  const [showSources, setShowSources] = useState(false)
  const [motifEcart, setMotifEcart] = useState('')

  useEffect(() => {
    fetchRecommandation()
  }, [sinistreId])

  async function fetchRecommandation() {
    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/doctrine/recommandation/${sinistreId}`)
      const data = await res.json()
      
      if (data.has_recommandation) {
        setRecommandation(data.recommandation)
      }
    } catch (err) {
      console.error('Error fetching recommandation:', err)
    } finally {
      setLoading(false)
    }
  }

  async function genererRecommandation() {
    setGenerating(true)
    setError(null)
    
    try {
      // Utiliser la nouvelle API KB si disponible
      const endpoint = useKB 
        ? `${API_URL}/api/doctrine/evaluer-sinistre-kb/${sinistreId}?sauvegarder=true`
        : `${API_URL}/api/doctrine/evaluer-sinistre`
      
      const options = useKB 
        ? { method: 'POST' }
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sinistre_id: sinistreId })
          }
      
      const res = await fetch(endpoint, options)
      const data = await res.json()
      
      if (data.erreur || data.detail) {
        setError(data.erreur || data.detail)
        toast.error('Erreur lors de l\'évaluation')
      } else {
        // Refresh to get the saved recommendation
        await fetchRecommandation()
        toast.success('Recommandation doctrine générée')
        onRecommandationUpdate?.()
      }
    } catch (err) {
      setError('Erreur de connexion au moteur de doctrine')
      toast.error('Erreur de connexion')
    } finally {
      setGenerating(false)
    }
  }

  async function validerRecommandation(statut) {
    try {
      const params = new URLSearchParams({
        statut,
        ...(statut === 'ecartee' && motifEcart ? { motif_ecart: motifEcart } : {})
      })
      
      const res = await fetch(
        `${API_URL}/api/doctrine/valider-recommandation/${recommandation.id}?${params}`,
        { method: 'POST' }
      )
      
      const data = await res.json()
      
      if (data.success) {
        toast.success(statut === 'suivie' ? 'Recommandation validée' : 'Recommandation écartée')
        setShowValidation(false)
        setMotifEcart('')
        await fetchRecommandation()
        onRecommandationUpdate?.()
      }
    } catch (err) {
      toast.error('Erreur lors de la validation')
    }
  }

  function formatCurrency(amount) {
    if (amount == null) return '—'
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount)
  }

  const recoConfig = recommandation ? RECOMMANDATIONS[recommandation.recommandation] : null
  const statutConfig = recommandation ? STATUTS[recommandation.statut] : null

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="doctrine-recommandation-block">
      <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-slate-50 flex items-center justify-between">
        <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
          <Scale size={16} className="text-indigo-600" />
          Recommandation Doctrine C2
        </h2>
        <button
          onClick={genererRecommandation}
          disabled={generating}
          data-testid="generer-reco-btn"
          className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
        >
          {generating ? (
            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <RefreshCw size={12} />
          )}
          {recommandation ? 'Réévaluer' : 'Évaluer'}
        </button>
      </div>

      <div className="p-5">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 mb-4">
            <AlertTriangle size={16} />
            {error}
          </div>
        )}

        {loading && (
          <div className="text-center py-6">
            <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-500">Chargement...</p>
          </div>
        )}

        {!recommandation && !loading && !generating && (
          <div className="text-center py-6">
            <Scale size={32} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">Aucune recommandation disponible</p>
            <p className="text-xs text-slate-400 mt-1">Cliquez sur "Évaluer" pour générer une recommandation</p>
          </div>
        )}

        {generating && (
          <div className="text-center py-6">
            <div className="w-10 h-10 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-500">Évaluation en cours...</p>
            <p className="text-xs text-slate-400 mt-1">Le moteur de doctrine analyse le sinistre</p>
          </div>
        )}

        {recommandation && !loading && !generating && (
          <div className="space-y-4">
            {/* Recommandation principale */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{recoConfig?.icon}</span>
                <div>
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold ${recoConfig?.color}`}>
                    {recoConfig?.label}
                  </span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded ${statutConfig?.color}`}>
                    {statutConfig?.label}
                  </span>
                </div>
              </div>
              
              {/* Confidence indicator */}
              <div className="text-right">
                <p className="text-xs text-slate-400">Confiance</p>
                <p className={`text-sm font-medium ${
                  recommandation.niveau_confiance === 'eleve' ? 'text-green-600' :
                  recommandation.niveau_confiance === 'moyen' ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {recommandation.niveau_confiance === 'eleve' ? 'Élevée' :
                   recommandation.niveau_confiance === 'moyen' ? 'Moyenne' : 'Faible'}
                </p>
              </div>
            </div>

            {/* Justification */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-400 mb-1">Justification</p>
              <p className="text-sm text-slate-700">{recommandation.justification}</p>
            </div>

            {/* Économie estimée */}
            {recommandation.economie_estimee != null && recommandation.economie_estimee > 0 && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <TrendingUp size={16} className="text-green-600" />
                <span className="text-sm text-green-700">
                  Économie estimée: <strong>{formatCurrency(recommandation.economie_estimee)}</strong>
                </span>
              </div>
            )}

            {/* Niveau de validation */}
            <div className="flex items-center gap-2">
              <HelpCircle size={14} className="text-slate-400" />
              <span className="text-xs text-slate-500">
                Validation requise: <strong>{
                  recommandation.niveau_validation_requis === 'gestionnaire' ? 'Gestionnaire' :
                  recommandation.niveau_validation_requis === 'senior' ? 'Senior' : 'Direction'
                }</strong>
              </span>
            </div>

            {/* Toggle details */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
            >
              {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {showDetails ? 'Masquer les détails' : 'Voir les détails'}
            </button>

            {/* Details */}
            {showDetails && recommandation.seuils_utilises && (
              <div className="p-3 bg-slate-50 rounded-lg text-xs space-y-2">
                <p className="font-medium text-slate-600">Seuils utilisés:</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>Rétention: {formatCurrency(recommandation.seuils_utilises.seuil_conservation)}</div>
                  <div>Amiable: {formatCurrency(recommandation.seuils_utilises.seuil_amiable)}</div>
                  <div>Assureur: {formatCurrency(recommandation.seuils_utilises.seuil_assureur)}</div>
                  <div>Franchise: {formatCurrency(recommandation.seuils_utilises.franchise_contrat)}</div>
                </div>
                <p className="text-slate-500 mt-2">
                  Montant estimé: {formatCurrency(recommandation.seuils_utilises.montant_estime)}
                </p>
                {recommandation.doctrine_utilisee && (
                  <p className="text-slate-500">
                    Doctrine: {recommandation.doctrine_utilisee.nom} (v{recommandation.doctrine_utilisee.version})
                  </p>
                )}
              </div>
            )}

            {/* Motif d'écart si écartée */}
            {recommandation.statut === 'ecartee' && recommandation.motif_ecart && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs text-red-600 mb-1">Motif d'écart</p>
                <p className="text-sm text-red-700">{recommandation.motif_ecart}</p>
              </div>
            )}

            {/* Validation buttons */}
            {recommandation.statut === 'generee' && (
              <div className="pt-3 border-t border-slate-100">
                {!showValidation ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => validerRecommandation('suivie')}
                      data-testid="suivre-reco-btn"
                      className="flex-1 px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 transition-colors"
                    >
                      <CheckCircle size={14} />
                      Suivre la recommandation
                    </button>
                    <button
                      onClick={() => setShowValidation(true)}
                      data-testid="ecarter-reco-btn"
                      className="px-3 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 flex items-center justify-center gap-2 transition-colors"
                    >
                      <XCircle size={14} />
                      Écarter
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <textarea
                      value={motifEcart}
                      onChange={(e) => setMotifEcart(e.target.value)}
                      placeholder="Motif de l'écart (obligatoire)..."
                      rows={2}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-red-500 resize-none"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => validerRecommandation('ecartee')}
                        disabled={!motifEcart.trim()}
                        className="flex-1 px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                      >
                        <XCircle size={14} />
                        Confirmer l'écart
                      </button>
                      <button
                        onClick={() => { setShowValidation(false); setMotifEcart('') }}
                        className="px-3 py-2 text-slate-600 text-sm hover:text-slate-800"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
